'use client'

import clsx from 'clsx'
import { HTMLAttributes, useState } from 'react'

import cl from './segmented-control.module.scss'

export interface SegmentedControlOption {
	label: string
	value: string
}

interface SegmentedControlProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onClick' | 'onChange'> {
	options?: SegmentedControlOption[]
	value?: string
	onChange?: (value: string) => void
	size?: 'sm' | 'md'
	disabled?: boolean
}

export const SegmentedControl = (props: SegmentedControlProps) => {
	const { options = [], value: externalValue, onChange, size = 'md', disabled, className, ...rest } = props

	const [internalValue, setInternalValue] = useState(options[0].value)
	const isControlled = externalValue !== undefined
	const value = isControlled ? externalValue : internalValue

	const handleClick = (value: string) => {
		if (disabled) return

		if (!isControlled) {
			setInternalValue(value)
		}

		onChange?.(value)
	}

	const rootClasses = clsx(cl.root, cl[size], className, {
		[cl.disabled]: disabled
	})

	if (!options.length) return null

	return (
		<>
			<div className={rootClasses} role="group" dir="ltr" tabIndex={0} {...rest}>
				{options.map(option => (
					<button
						type="button"
						role="radio"
						aria-checked={value === option.value}
						aria-disabled={disabled}
						tabIndex={-1}
						key={option.value}
						className={clsx(cl.button, { [cl.button__active]: value === option.value })}
						onClick={() => handleClick(option.value)}>
						{option.label}
					</button>
				))}
			</div>
		</>
	)
}
